// A page for one of today's link paths (oldLinks.ts): the redirect it leads
// to, replacing the old address, or the one line that says where the thing
// went. It works from a cold start (a link opening the app) as the frame
// draws it only once the board has loaded, and within the app as any route.
import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { errorMessage } from '../api/client'
import { NotFound } from '../components/layout'
import { useDocumentTitle } from '../components/useDocumentTitle'
import { OldPlanAddress } from '../plans/PlanPage'
import { useBoardState, useStore } from '../store/context'
import { repositoryUrl, type Repository } from './movedDocuments'
import { readOldLink } from './oldLinks'
import { linkWords as w } from './words'

export function OldLinkPage() {
  const { pathname, search, hash } = useLocation()
  const state = useBoardState()
  const link = readOldLink(pathname, search, hash, state)
  if (!link) return <NotFound what="page" />
  switch (link.kind) {
    case 'redirect': return <Navigate to={link.to} replace />
    case 'file': return <OldFile sha256={link.sha256} />
    case 'plan': return <OldPlanAddress id={link.id} />
    case 'moved': return <Moved path={link.path} repository={link.repository} />
    case 'gone': return <Gone what={link.what} />
  }
}

/** A document the app no longer holds: where it is in the repository. */
export function Moved({ path, repository }: { path: string; repository: Repository }) {
  useDocumentTitle(w.movedTitle)
  return (
    <div className="state-message">
      <p>{w.moved} <a href={repositoryUrl(path, repository)} target="_blank" rel="noreferrer noopener">{path || w.repository}</a></p>
    </div>
  )
}

function Gone({ what }: { what: 'request' | 'run' }) {
  useDocumentTitle(w.goneTitle)
  return <div className="state-message"><p>{what === 'request' ? w.requestGone : w.runGone}</p></div>
}

/** Read with the current member's authentication, including in the Mac shell. */
function OldFile({ sha256 }: { sha256: string }) {
  const store = useStore()
  const { attachments } = useBoardState()
  const [file, setFile] = useState<{ url: string; mime: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  useDocumentTitle('File')
  useEffect(() => {
    let current = true
    let url: string | undefined
    setFile(null)
    setError(null)
    void store.attachmentFile(sha256).then((blob) => {
      if (!current) return
      url = URL.createObjectURL(blob)
      setFile({ url, mime: blob.type })
    }).catch((err) => { if (current) setError(errorMessage(err)) })
    return () => { current = false; if (url) URL.revokeObjectURL(url) }
  }, [store, sha256])
  if (error) return <p role="alert">{error}</p>
  if (!file) return <p>Loading file…</p>
  const extension = file.mime === 'application/pdf' ? 'pdf' : file.mime === 'image/jpeg' ? 'jpg' : 'png'
  const name = [...attachments.values()].find(a => a.old_sha256 === sha256)?.title ?? `file.${extension}`
  return <div className="state-message">
    {file.mime.startsWith('image/') && <img src={file.url} alt="Attached file" className="attachment-preview" />}
    <a href={file.url} download={name}>Download file</a>
  </div>
}
